/**
 * authorizedBy — "Authorized by (full name)" for the Change in Contact (CIC) workflow.
 * CHG-0186 (Bill 2026-11-04).
 *
 * Bill: "The CIC workflow does not need to limit the Initials of the verified customer anymore; that was
 * a legacy Core Director UI limitation. Replace Initials with Full Name and do not limit the entry, as it
 * goes into a note, unless there is a note total limit." The note limit is real (250 characters per Core
 * note, CustomerProfileChangePlanner.NoteMaxChars) and the name rides in the trailer of every note, so
 * the entry is capped at 100 characters (any real name) — no 2-character minimum, never upper-cased.
 *
 * Prefill (Bill's ruling): on an IN-BRANCH application created by an officer, the field starts with the
 * FIRST-NAMED customer on the application — the person in front of the officer, who authorizes every
 * change on that application, beneficiaries included. Online applications (imported / legacy JotForm),
 * Customer Management and the address-change dialogs (no application) start blank.
 *
 * Wire: the value still travels as `customerInitials` (profile-change apply, address-change note) so a
 * client and server deployed at different times keep working; only its meaning changed.
 */
import { personName, unwrapCollection } from '@/utils/cartLabel';

/** Matches CustomerProfileChangePlanner.AuthorizedByMaxChars on the server. */
export const AUTHORIZED_BY_MAX = 100;

export const AUTHORIZED_BY_LABEL = 'Authorized by (full name)';

export const AUTHORIZED_BY_PREFILL_HINT =
  'Pre-filled with the first customer on this application. Confirm this is who authorized the change, or change it.';

/**
 * Cart origins an officer creates with the customer in the branch: the New Account wizard
 * (useCartBootstrap → 'fsr-inbranch'; 'fsr-self' is the same officer-created draft), and the lobby's
 * internal entry ('in-branch'). Everything else is started by the customer (online / banno / mobile /
 * kiosk) or not face to face (phone).
 */
const OFFICER_IN_BRANCH_ORIGINS: ReadonlySet<string> = new Set(['fsr-inbranch', 'fsr-self', 'in-branch']);

export function isOfficerInBranchCart(origin: string | null | undefined): boolean {
  return OFFICER_IN_BRANCH_ORIGINS.has((origin ?? '').trim().toLowerCase());
}

/** Roles that are never the person authorizing (they are added on the authorizer's behalf). */
const NOT_AUTHORIZER_ROLES: ReadonlySet<string> = new Set(['beneficiary', 'pod']);

/** Trim, collapse inner whitespace, keep case — the same shaping the server applies. */
export function normalizeAuthorizedBy(value: string): string {
  return value.trim().split(/\s+/).filter(Boolean).join(' ');
}

/** Valid for submit: within the cap, and present when the verification method needs it. */
export function authorizedByOk(value: string, required: boolean): boolean {
  const v = normalizeAuthorizedBy(value);
  if (v.length > AUTHORIZED_BY_MAX) return false;
  return !required || v.length > 0;
}

/**
 * The prefill for an application's Change in Contact step: the first-named customer on an officer-created
 * in-branch application, else ''. `fallbackName` is used when the application has no named person yet
 * (the first customer is being added right now, so they are the first-named customer).
 */
export function authorizedByPrefill(
  cart: { origin?: string | null; peopleJson?: string | null } | null | undefined,
  fallbackName?: string,
): string {
  if (!cart || !isOfficerInBranchCart(cart.origin)) return '';
  const people = unwrapCollection(cart.peopleJson, 'people');
  const named = people
    .map((p) => ({ name: personName(p), role: String(((p ?? {}) as Record<string, unknown>)['role'] ?? '').toLowerCase() }))
    .filter((p) => p.name.length > 0);
  const first = named.find((p) => !NOT_AUTHORIZER_ROLES.has(p.role)) ?? named[0];
  const name = normalizeAuthorizedBy(first?.name ?? fallbackName ?? '');
  return name.slice(0, AUTHORIZED_BY_MAX);
}
